// ui
import { Container, Card, Image, Button, LoadingOverlay, Loader } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import styles from './styles.module.scss';
import { useEffect, useRef, useState } from 'react';

// next
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import { setCurrentGame, refreshGameList } from '@/store/features/gameSlice';
import { gameClicked } from "@/utils/event";

export default function GameGrid({ gameList = [], loading = false, hasMore = false, onLoadMore }) {
    const router = useRouter();
    const dispatch = useDispatch();
    const [opened, { toggle }] = useDisclosure(true);
    const loadingRef = useRef(null);
    const imageObserver = useRef(null);

    // 创建共享的 IntersectionObserver
    useEffect(() => {
        imageObserver.current = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const img = entry.target;
                        const dataSrc = img.getAttribute('data-src');
                        if (dataSrc) {
                            img.src = dataSrc;
                            img.removeAttribute('data-src');
                            imageObserver.current.unobserve(img);
                        }
                    }
                });
            },
            {
                rootMargin: '50px 0px',
                threshold: 0.1
            }
        );

        return () => {
            if (imageObserver.current) {
                imageObserver.current.disconnect();
            }
        };
    }, []);

    // 设置 Intersection Observer
    useEffect(() => {
        if (!hasMore) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !loading) {
                    onLoadMore?.();
                }
            },
            { threshold: 0.1 }
        );

        const currentRef = loadingRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [hasMore, loading, onLoadMore]);

    const ImageWithLazy = ({ src, alt, className }) => {
        const [isLoaded, setIsLoaded] = useState(false);
        const imgRef = useRef();

        useEffect(() => {
            if (imgRef.current && imageObserver.current) {
                imgRef.current.setAttribute('data-src', src);
                imageObserver.current.observe(imgRef.current);
            }

            return () => {
                if (imgRef.current && imageObserver.current) {
                    imageObserver.current.unobserve(imgRef.current);
                }
            };
        }, [src]);

        return (
            <div 
                className={className}
                style={{
                    position: 'relative',
                    backgroundColor: '#f0f0f0',
                    width: '100%',
                    height: '100%',
                    minHeight: '200px'
                }}
            >
                <img
                    ref={imgRef}
                    src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7" // 透明占位图
                    alt={alt}
                    loading={'lazy'}
                    decoding={'async'}
                    className={`${className} ${isLoaded ? styles.loaded : styles.loading}`}
                    onLoad={(e) => {
                        // 只有当加载了真实图片时才设置 isLoaded
                        if (e.target.src !== "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7") {
                            setIsLoaded(true);
                        }
                    }}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        opacity: isLoaded ? 1 : 0,
                    }}
                />
            </div>
        );
    };

    // 加载框
    if (!gameList.length && loading) {
        return (
            <Container className={`${styles.container} ${styles.loading}`}>
                <LoadingOverlay
                    visible={opened}
                    zIndex={2000}
                    overlayProps={{ radius: 'md', opacity: 0 }}
                    loaderProps={{ color: 'white', type: 'bars' }}
                />
            </Container>
        );
    }

    // 不存在游戏数据时
    if (!gameList.length && !loading) {
        const handleBackHome = () => {
            dispatch(refreshGameList());
        };

        return (
            <Container className={`${styles.container}`}>
                <div className={styles.image_wrapper}>
                    <Image
                        src="/images/icons/common/404.png"
                        alt="404 Error"
                        width={600}
                        height={200}
                        className={styles.error_image}
                    />
                    <div className={styles.remind}>
                        No Data
                    </div>
                </div>
            </Container>
        );
    }

    const handleGameClick = (game) => {
        // TODO： 测试GA的关键事件
        if (window) {
            window.gtag('event', 'click_game');
            window.gtag('event', 'conversion', {
                'send_to': 'AW-17085889980/P17JCLv42coaELz7l9M_'
            });
        }
        // 记录游戏的点击
        gameClicked({path: game.path});
        // 保存当前游戏数据到Redux, 避免重复请求
        dispatch(setCurrentGame({...game, clicked_count: game.clicked_count + 1}));
        // 触发刷新
        dispatch(refreshGameList());
        // 跳转到游戏详情页
        router.push(`/game/${game.path}`);
    };

    return (
        <>
            <div className={styles.grid_container}>
                {gameList.map((game, index) => (
                    <a
                        title={game.name}
                        key={`${game.id}`}
                        href={'/game/' + game.path}
                        onClick={(e) => {
                            // 只阻止直接点击，允许其他鼠标事件（如右键菜单）
                            if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
                                e.preventDefault();
                                handleGameClick(game);
                            }
                        }}
                    >
                        <Card
                            key={`${game.id}-${index}`}
                            className={`${styles.card} ${styles[game.id]}`}
                            shadow="sm"
                            padding={0}
                            radius="md"
                        >
                            <Card.Section className={styles.card_section}>
                                {!game.loading ? (
                                    <>
                                        <ImageWithLazy
                                            src={game.cover_image}
                                            alt={game.name}
                                            className={styles.image}
                                        />
                                    </>
                                    ) : (
                                    <div className={styles.cover_loader}>
                                        <Loader color="blue" />
                                    </div>
                                )}
                                <div className={`${styles.image_name} ${styles.font_light}`}>
                                    {game.name}
                                </div>
                                </Card.Section>
                            </Card>
                    </a>
                ))}
            </div>
            
            {/* 加载更多指示器 */}
            {hasMore && (
                <div ref={loadingRef} className={styles.loading_more}>
                    <LoadingOverlay
                        visible={loading}
                        zIndex={2000}
                        overlayProps={{ radius: 'md', opacity: 0 }}
                        loaderProps={{ color: 'white', type: 'bars' }}
                    />
                </div>
            )}
        </>
    );
}